import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { AdminPageContainer } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import DepartmentDialog from '../../components/admin/DepartmentDialog';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { getDepartments, createDepartment, updateDepartment, deleteDepartment } from '../../redux/ducks/department';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../redux/slices/alerts';

const ITEMS_PER_PAGE = 20;

const SearchRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const PaginationRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
}));

const AdminDepartments = () => {
    const dispatch = useDispatch();
    const [departments, setDepartments] = useState([]);
    const [filteredDepartments, setFilteredDepartments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [departmentToDelete, setDepartmentToDelete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        loadDepartments();
    }, []);

    useEffect(() => {
        filterDepartments();
    }, [searchTerm, departments]);

    const loadDepartments = () => {
        setLoading(true);
        dispatch(getDepartments(0, 1000, (data) => {
            setDepartments(data || []);
            setLoading(false);
        }));
    };

    const filterDepartments = () => {
        let filtered = departments;

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    d.name?.toLowerCase().includes(term) ||
                    d.description?.toLowerCase().includes(term) ||
                    d.email?.toLowerCase().includes(term)
            );
        }

        setFilteredDepartments(filtered);
        setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
        setPage(1);
    };

    const handleAddClick = () => {
        setDialogMode('create');
        setSelectedDepartment(null);
        setDialogOpen(true);
    };

    const handleEditClick = (department) => {
        setDialogMode('edit');
        setSelectedDepartment(department);
        setDialogOpen(true);
    };

    const handleDeleteClick = (department) => {
        setDepartmentToDelete(department);
        setConfirmDialogOpen(true);
    };

    const handleSave = (formData) => {
        if (dialogMode === 'create') {
            dispatch(createDepartment(formData, (response) => {
                if (!response) return;
                dispatch(showAlert({ severity: 'success', message: 'Department created successfully' }));
                loadDepartments();
            }));
        } else {
            dispatch(updateDepartment(selectedDepartment._id, formData, (response) => {
                if (!response) return;
                dispatch(showAlert({ severity: 'success', message: 'Department updated successfully' }));
                loadDepartments();
            }));
        }
    };

    const handleConfirmDelete = () => {
        dispatch(deleteDepartment(departmentToDelete._id, (response) => {
            if (!response) return;
            dispatch(showAlert({ severity: 'success', message: 'Department deleted successfully' }));
            loadDepartments();
        }));
        setConfirmDialogOpen(false);
        setDepartmentToDelete(null);
    };

    const getPaginatedDepartments = () => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredDepartments.slice(startIndex, endIndex);
    };

    const getManagerName = (department) => {
        if (!department.manager) return '—';
        return department.manager.fullName || 'Unknown';
    };

    const getUserCount = (department) => {
        return department.users?.length || 0;
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Departments Management"
                subtitle="Manage departments with user and manager assignments"
                actions={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        Add Department
                    </Button>
                }
            />

            <SearchRow>
                <TextField
                    fullWidth
                    placeholder="Search departments by name, description, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </SearchRow>

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : filteredDepartments.length === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        {searchTerm
                            ? 'No departments found matching your search.'
                            : 'No departments created yet. Click "Add Department" to create one.'}
                    </Typography>
                </EmptyState>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Department Name</strong></TableCell>
                                    <TableCell><strong>Description</strong></TableCell>
                                    <TableCell><strong>Email</strong></TableCell>
                                    <TableCell><strong>Manager</strong></TableCell>
                                    <TableCell align="center"><strong>Users</strong></TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getPaginatedDepartments().map((department) => (
                                    <TableRow key={department._id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {department.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {department.description || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {department.email || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {getManagerName(department)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={getUserCount(department)}
                                                size="small"
                                                color={getUserCount(department) > 0 ? 'primary' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleEditClick(department)} color="primary">
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDeleteClick(department)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <PaginationRow>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                            />
                        </PaginationRow>
                    )}
                </>
            )}

            <DepartmentDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                department={selectedDepartment}
                mode={dialogMode}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={() => {
                    setConfirmDialogOpen(false);
                    setDepartmentToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                message={`Are you sure you want to delete the department "${departmentToDelete?.name}"? This action cannot be undone.`}
            />
        </AdminPageContainer>
    );
};

export default AdminDepartments;
